import 'lazysizes';
import { createApp } from 'vue';
import { createStore } from 'vuex';

import { hoistJsonScripts } from '@Foundation/utilities';

// Plugins for zone-level customizations
import loadZoneCustomizations from '@refapp/zone-plugin-loader';
import init from '@refapp/sisyphus';

// Sitecore-Vue bridge
import SitecoreVueAdapter from '@Foundation/sitecore/SitecoreVueAdapter';

// Helpers
import { requireAll } from '@Foundation/utilities/helpers';

// Foundation
import Foundation from '@Foundation';
import LoadScriptModule from '@Foundation/loadscript';
import eventBus, { exposeGlobals } from '@refapp/eventbus-js';
import { tag } from '@Foundation/analyticsHandler/directives/tag';

// Feature
import AccessibilityStore from '@Foundation/accessibilityStore';
import OapPopin from '@Feature/OapPopin';
import ArticleShareItem from '@Feature/PageContentExt';
import BeautyMagModule from '@Feature/BeautyMag';
import CarouselModule from '@Feature/Carousel';
import FilterModule from '@Feature/Filter';
import GenericForm from '@Feature/GenericForm';
import HeaderLogo from '@Feature/HeaderLogo';
import InstagramComponent from '@Feature/Instagram';
import LiveChat from '@Feature/LiveChat';
import NavigationModule from '@Feature/Navigation';
import NewsletterModule from '@Feature/NewsLetter';
import OapAccountNavigation from '@Feature/OapAccountNavigation';
import OapBeautyGeniusWrapper from '@Feature/OapBeautyGeniusWrapper';
import OapBeautyGeniusCard from '@Feature/OapBeautyGeniusCard';
import OapCard from '@Feature/OapCard';
import OapCardPopin from '@Feature/OapCardPopin';
import OapCarouselControls from '@Feature/CarouselControls';
import OapChannelAdvisor from '@Feature/OapChannelAdvisor';
import OapCountdown from '@Feature/OapCountdown';
import OapContrastSwitcher from '@Feature/OapContrastSwitcher';
import OapEventfulTextInput from '@Feature/OapEventfulTextInput';
import OapFavorite from '@Feature/OapFavorite';
import OapFloatingIsland from '@Feature/OapFloatingIsland';
import OapFocusTrap from '@Feature/OapFocusTrap';
import OapGeolocationPopin from '@Feature/OapGeolocationPopin';
import OapIframe from '@Feature/OapIframe';
import OapMediaCarousel from '@Feature/OapMediaCarousel';
import OapPromoBar from '@Feature/OapPromoBar';
import OapPopup from '@Feature/OapPopup';
import OapRating from '@Feature/OapRating';
import OapRoutineBuilder from '@Feature/RoutineBuilder';
import OapScroller from '@Feature/OapScroller';
import OapSlider from '@Feature/OapSlider';
import OapTableOfContents from '@Feature/OapTableOfContents';
import OapTabComponent from '@Feature/OapTab';
import OapStories from '@Feature/OapStories';
import OapToastNotification from '@Feature/OapToastNotification';
import OneTrustCookieHelper from '@Feature/OneTrust';
import Overlay from '@Feature/Overlay';
import Search from '@Feature/Search';
import SearchSuggestions from '@Feature/SearchSuggestions';
import Sfmc from '@Feature/SFMC';
import Slider from '@Feature/Slider';
import VideoPlaceholder from '@Feature/VideoPlaceholder';
import VueModels from '@Feature/VueModels';
import OapPageNavigation from '@Feature/OapPageNavigation';
import OapPromoCard from '@Feature/OapPromoCard';
import FooterNewsletterSignupModal from '@Feature/FooterNewsletterSignupModal';
import FloatingSidebar from '@Feature/FloatingSidebar';
import OapMikMak from '@Feature/OapMikMak';
import OapRegisterNowPopup from '@Feature/OapRegisterNowPopup';
import WishList from '@Feature/WishList';

// 3rd party
import { Tabs, Tab } from 'vue3-tabs-component';
import ToastedPlugin from '@Feature/OapToastNotification/code/plugin';

const VueTabsPlugin = {
  install(app) {
    app.component('Tabs', Tabs);
    app.component('Tab', Tab);
  },
};

exposeGlobals();

const app = createApp({});
// TODO: To be enabled if removing imports for eventBus makes sense
// app.config.globalProperties.eventBus = eventBus;
app.config.compilerOptions.whitespace = 'preserve';
app.config.performance = true;

// Bootstrap Vue
app.use(ToastedPlugin);
app.use(VueTabsPlugin);

// Register directives
Object.entries({
  ...Foundation.directives,
  ...LoadScriptModule.directives,
  ...NavigationModule.directives,
  ...Search.directives,
  tag,
}).forEach(([name, directive]) => app.directive(name, directive));

// Register component
// TODO: Object.values ???
Object.entries({
  ...ArticleShareItem.components,
  ...BeautyMagModule.components,
  ...CarouselModule.components,
  ...FilterModule.components,
  ...Foundation.components,
  ...GenericForm.components,
  ...HeaderLogo.components,
  ...InstagramComponent.components,
  ...LiveChat.components,
  ...LoadScriptModule.components,
  ...NavigationModule.components,
  ...NewsletterModule.components,
  ...OapAccountNavigation.components,
  ...OapBeautyGeniusWrapper.components,
  ...OapBeautyGeniusCard.components,
  ...OapCard.components,
  ...OapCardPopin.components,
  ...OapCarouselControls.components,
  ...OapChannelAdvisor.components,
  ...OapCountdown.components,
  ...OapContrastSwitcher.components,
  ...OapEventfulTextInput.components,
  ...OapFavorite.components,
  ...OapFloatingIsland.components,
  ...OapFocusTrap.components,
  ...OapGeolocationPopin.components,
  ...OapIframe.components,
  ...OapMediaCarousel.components,
  ...OapPromoBar.components,
  ...OapPopup.components,
  ...OapRating.components,
  ...OapRoutineBuilder.components,
  ...OapTableOfContents.components,
  ...OapTabComponent.components,
  ...OapScroller.components,
  ...OapSlider.components,
  ...OapStories.components,
  ...OapToastNotification.components,
  ...OneTrustCookieHelper.components,
  ...Overlay.components,
  ...SearchSuggestions.components,
  ...Sfmc.components,
  ...Slider.components,
  ...VideoPlaceholder.components,
  ...VueModels.components,
  ...OapPopin.components,
  ...OapPageNavigation.components,
  ...OapPromoCard.components,
  ...FooterNewsletterSignupModal.components,
  ...FloatingSidebar.components,
  ...OapMikMak.components,
  ...OapRegisterNowPopup.components,
  ...WishList.components,
}).forEach(([name, component]) => app.component(name, component));

Object.entries({
  ...Foundation.filters,
}).forEach(([name, filter]) => app.filter(name, filter));

const store = createStore({
  plugins: [],
  modules: {
    ...AccessibilityStore.modules,
    ...OapFavorite.modules,
    ...FilterModule.modules,
  },
});

app.use(store);

/**
 * Initialize any zone-level customizations.
 * Every zone/country can create their own Vue components and make them available
 * to their code through the use of a Vue.js Plugin
 *
 * @see https://vuejs.org/guide/reusability/plugins.html#writing-a-plugin
 * */
loadZoneCustomizations(app, store);
// Initialize third party loader
init();

requireAll(require.context('../../../Icons/', true, /\.svg$/));

document.addEventListener('lazybeforeunveil', (event) =>
  eventBus.emit('image.lazybeforeunveil', event)
);

window.RefAppStoreLocator && app.use(window.RefAppStoreLocator);

hoistJsonScripts();

SitecoreVueAdapter.initializeVueApp(app.mount.bind(app, '#main-container'));
